import { AlertIcon, LinkIcon, MarkGithubIcon, PersonIcon } from "@primer/octicons-react";
import { useSearchParams } from "react-router-dom";
import { useAction, useMe } from "../api/hooks";
import type { Connection } from "../api/types";
import { AvatarImage } from "../components/AvatarImage";
import { Empty, ErrorMessage, Loading, Query, TimeAgo } from "../components/common";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ProviderButton } from "./Login";

/** The signed-in person and the GitHub accounts imports and remotes use. */
export function AccountPanel() {
  const me = useMe();
  const [params] = useSearchParams();
  if (me.isLoading) return <Loading />;
  return (
    <div>
      {params.get("error") && (
        <Alert variant="error" className="mt-0">
          <AlertTitle>
            <AlertIcon /> Connecting failed
          </AlertTitle>
          {params.get("error")}
        </Alert>
      )}
      <Query q={me}>
        {(m) =>
          !m.user ? (
            <Alert variant="info" className="mt-0">
              You signed in with an access token ({m.actor.sub}). Sign in with GitHub to connect accounts.
            </Alert>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <PersonIcon /> Profile
                </CardHeader>
                <CardContent className="flex items-center gap-4">
                  <AvatarImage src={m.user.avatar_url} name={m.user.name} size={56} />
                  <div className="min-w-0">
                    <div className="truncate text-lg font-semibold">{m.user.display}</div>
                    <div className="text-muted-foreground">
                      <span className="font-mono">{m.user.name}</span>
                      {m.user.email && <> · {m.user.email}</>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge tone={m.user.role === "admin" ? "purple" : m.user.role === "write" ? "green" : "default"}>{m.user.role}</Badge>
                      {m.user.role === "read" && "Ask an admin for write or admin access to land changes or create repositories."}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <LinkIcon /> Connections
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Signing in only reads your profile. Grant repository access to import or sync without a token: repositories you import or link are then bound
                  to your connection for their host, and Bee clones, pushes, pulls and opens batch pull requests with its token, refreshed automatically.
                  Disconnecting erases the token; those repositories fall back to the server's environment token.
                </CardContent>
                {m.connections.length ? (
                  <Table>
                    <TableHeader>
                      <tr>
                        <TableHead>Account</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="max-md:hidden">Updated</TableHead>
                        <TableHead />
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {m.connections.map((c) => (
                        <ConnectionRow key={c.id} c={c} provider={m.providers.find((p) => p.id === c.provider && p.host === c.host)} />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Empty icon={LinkIcon}>No connections.</Empty>
                )}
                {m.providers.some((p) => !m.connections.some((c) => c.provider === p.id && c.host === p.host)) && (
                  <CardContent className="flex flex-wrap gap-2">
                    {m.providers
                      .filter((p) => !m.connections.some((c) => c.provider === p.id && c.host === p.host))
                      .map((p) => (
                        <div key={p.id} className="w-64">
                          <ProviderButton provider={p} next="/profile?tab=account" connect label={`Connect ${p.name}`} />
                        </div>
                      ))}
                  </CardContent>
                )}
              </Card>
            </>
          )
        }
      </Query>
    </div>
  );
}

function ConnectionRow({ c, provider }: { c: Connection; provider?: { id: Connection["provider"]; name: string; host: string } }) {
  const del = useAction();
  const Icon = MarkGithubIcon;
  return (
    <TableRow>
      <TableCell>
        <span className="inline-flex items-center gap-2">
          <Icon /> <span className="font-semibold">{c.login}</span> <span className="text-xs text-muted-foreground">{c.host}</span>
        </span>
      </TableCell>
      <TableCell>
        {c.status === "ok" ? (
          <Badge tone="green">Connected</Badge>
        ) : c.status === "sign_in_only" ? (
          <Badge title="You signed in with this account but have not granted access to its repositories">Sign-in only</Badge>
        ) : c.status === "revoked" ? (
          <Badge tone="red" title="The provider refused the token: reconnect to keep imports and sync working">
            Token refused
          </Badge>
        ) : (
          <Badge>Disconnected</Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground max-md:hidden">
        <TimeAgo ms={c.updated} />
      </TableCell>
      <TableCell className="text-right">
        {c.status !== "ok" && provider ? (
          <div className={c.status === "sign_in_only" ? "inline-block w-56" : "inline-block w-36"}>
            <ProviderButton provider={provider} next="/profile?tab=account" connect label={c.status === "sign_in_only" ? "Grant repository access" : "Reconnect"} />
          </div>
        ) : c.status === "ok" ? (
          <ConfirmDialog
            title={`Disconnect ${c.login} on ${c.host}?`}
            description="bee erases the token. Repositories bound to it use the server's environment token instead, if there is one; imports and sync fail otherwise."
            confirmLabel="Disconnect"
            onConfirm={() => del.mutate({ method: "DELETE", path: `/v1/me/connections/${c.id}`, body: {} })}
          >
            <Button type="button" size="sm" variant="danger" disabled={del.isPending}>
              Disconnect
            </Button>
          </ConfirmDialog>
        ) : null}
        {del.error ? <ErrorMessage error={del.error} /> : null}
      </TableCell>
    </TableRow>
  );
}
